import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users as UsersIcon,
  ShieldCheck,
  ScrollText,
  Search,
  Pencil,
  Trash2,
  Plus,
  Copy,
  X,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { getCurrentUser } from '../lib/auth.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { Badge, type BadgeVariant } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';
// Inline permissions to avoid @map/shared build dependency
const PERMISSIONS: Record<string, readonly string[]> = {
  campaigns:   ['read', 'write', 'delete', 'send'],
  contacts:    ['read', 'write', 'delete', 'import', 'export'],
  automations: ['read', 'write', 'manage'],
  analytics:   ['read', 'export'],
  settings:    ['read', 'manage'],
  loyalty:     ['read', 'write', 'manage'],
  cms:         ['read', 'write', 'manage'],
};
type Resource = keyof typeof PERMISSIONS;

// ─── Types ────────────────────────────────────────────────────────────────────

interface TenantUser {
  id: string;
  tenantId: string;
  email: string;
  role: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  roles: { id: string; name: string }[];
}

interface Role {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  scopeConstraints: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface RoleTemplate {
  key: string;
  name: string;
  description: string;
  permissions: string[];
}

interface AuditLog {
  id: string;
  tenantId: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  ip: string | null;
  ipAddress: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface AuditLogsResponse {
  data: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

type Tab = 'users' | 'roles' | 'audit';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDate(d: string) {
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ROLE_BADGE_COLORS: Record<string, BadgeVariant> = {
  tenant_admin: 'purple',
  editor: 'info',
  viewer: 'default',
};

// All resources and their actions (from shared permissions)
const RESOURCE_ACTIONS: Record<Resource, readonly string[]> = PERMISSIONS;

// ─── Component ────────────────────────────────────────────────────────────────

export function TeamPage() {
  const user = getCurrentUser();
  const tenantId = user?.tenantId;
  const toast = useToast();
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>('users');

  if (!tenantId) {
    return (
      <EmptyState
        icon={<UsersIcon size={26} />}
        title="No tenant selected"
        description="You need to be impersonating a tenant to manage the team."
      />
    );
  }

  return (
    <>
      <PageHeader title="Team" description="Manage users, roles, and review audit logs." />

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-zinc-200 mb-6">
        {([
          { key: 'users' as Tab, label: 'Users', icon: UsersIcon },
          { key: 'roles' as Tab, label: 'Roles', icon: ShieldCheck },
          { key: 'audit' as Tab, label: 'Audit Log', icon: ScrollText },
        ]).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === key
                ? 'border-zinc-900 text-zinc-900'
                : 'border-transparent text-zinc-400 hover:text-zinc-600'
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'users' && <UsersTab tenantId={tenantId} />}
      {tab === 'roles' && <RolesTab tenantId={tenantId} />}
      {tab === 'audit' && <AuditTab tenantId={tenantId} />}
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 1: Users
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface UserForm {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}

const EMPTY_USER_FORM: UserForm = { email: '', firstName: '', lastName: '', password: '' };

function UsersTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState(EMPTY_USER_FORM);
  const [inviteRoleIds, setInviteRoleIds] = useState<string[]>([]);
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [editRoleIds, setEditRoleIds] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<TenantUser | null>(null);

  // ── Queries ──
  const { data: users = [], isLoading } = useQuery<TenantUser[]>({
    queryKey: ['team-users', tenantId],
    queryFn: () => api.get(`/sa/tenants/${tenantId}/users`).then((r) => r.data),
  });

  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ['team-roles', tenantId],
    queryFn: () => api.get(`/sa/tenants/${tenantId}/roles`).then((r) => r.data),
  });

  const filtered = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.firstName ?? '').toLowerCase().includes(q) ||
        (u.lastName ?? '').toLowerCase().includes(q),
    );
  }, [users, search]);

  // ── Mutations ──
  const inviteMutation = useMutation({
    mutationFn: (body: UserForm & { roleId?: string }) => api.post(`/sa/tenants/${tenantId}/users`, body),
    onSuccess: async (_res, _vars) => {
      // If multiple roles selected we need to assign them after creation
      // The API only supports a single roleId on create
      qc.invalidateQueries({ queryKey: ['team-users', tenantId] });
      setShowInvite(false);
      setInviteForm(EMPTY_USER_FORM);
      setInviteRoleIds([]);
      toast.success('User created');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      api.patch(`/sa/tenants/${tenantId}/users/${userId}`, { isActive }),
    onSuccess: (_data, { isActive }) => {
      qc.invalidateQueries({ queryKey: ['team-users', tenantId] });
      toast.success(isActive ? 'User enabled' : 'User disabled');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/sa/tenants/${tenantId}/users/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-users', tenantId] });
      setDeleteTarget(null);
      toast.success('User deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (body: { userId: string; data: Record<string, unknown> }) =>
      api.patch(`/sa/tenants/${tenantId}/users/${body.userId}`, body.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-users', tenantId] });
      toast.success('User updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const assignRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.post(`/sa/tenants/${tenantId}/users/${userId}/roles`, { roleId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-users', tenantId] }),
    onError: (err) => toast.error(extractError(err)),
  });

  const revokeRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      api.delete(`/sa/tenants/${tenantId}/users/${userId}/roles/${roleId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-users', tenantId] }),
    onError: (err) => toast.error(extractError(err)),
  });

  function openEdit(u: TenantUser) {
    setEditingUser(u);
    setEditForm({
      firstName: u.firstName ?? '',
      lastName: u.lastName ?? '',
      email: u.email,
      password: '',
    });
    setEditRoleIds(u.roles.map((r) => r.id));
  }

  async function handleSaveEdit() {
    if (!editingUser) return;
    const data: Record<string, unknown> = {};
    if (editForm.firstName !== (editingUser.firstName ?? '')) data.firstName = editForm.firstName;
    if (editForm.lastName !== (editingUser.lastName ?? '')) data.lastName = editForm.lastName;
    if (editForm.email !== editingUser.email) data.email = editForm.email;
    if (editForm.password) data.password = editForm.password;

    if (Object.keys(data).length > 0) {
      await updateMutation.mutateAsync({ userId: editingUser.id, data });
    }

    // Sync roles: add new ones, remove revoked ones
    const currentIds = new Set(editingUser.roles.map((r) => r.id));
    const targetIds = new Set(editRoleIds);
    for (const rid of editRoleIds) {
      if (!currentIds.has(rid)) {
        await assignRoleMutation.mutateAsync({ userId: editingUser.id, roleId: rid });
      }
    }
    for (const rid of editingUser.roles.map((r) => r.id)) {
      if (!targetIds.has(rid)) {
        await revokeRoleMutation.mutateAsync({ userId: editingUser.id, roleId: rid });
      }
    }

    setEditingUser(null);
    toast.success('User updated');
  }

  async function handleInvite() {
    // Create user with first role (API supports single roleId)
    await inviteMutation.mutateAsync({
      ...inviteForm,
      roleId: inviteRoleIds[0] || undefined,
    });
    // If multiple roles, assign remaining after creation
    // The user list will be refetched; for simplicity we handle this on next query
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-9 pl-9 pr-3 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
              <X size={14} />
            </button>
          )}
        </div>
        <Button size="sm" onClick={() => setShowInvite(true)}>
          <Plus size={14} /> Invite User
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <SkeletonRows count={5} cols={5} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<UsersIcon size={26} />} title="No users found" description={search ? 'Try a different search.' : 'Invite your first team member.'} />
      ) : (
        <div className="border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500 text-left text-xs font-medium">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Roles</th>
                <th className="px-4 py-2.5">Last Login</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-zinc-50/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-zinc-900">
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length === 0 ? (
                        <span className="text-xs text-zinc-400">No roles</span>
                      ) : (
                        u.roles.map((r) => (
                          <Badge key={r.id} variant={ROLE_BADGE_COLORS[r.name] ?? 'info'}>
                            {r.name}
                          </Badge>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{relativeTime(u.lastLoginAt)}</td>
                  <td className="px-4 py-3">
                    <Badge variant={u.isActive ? 'success' : 'default'} dot>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        title="Edit"
                        onClick={() => openEdit(u)}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        title={u.isActive ? 'Deactivate' : 'Activate'}
                        onClick={() => toggleMutation.mutate({ userId: u.id, isActive: !u.isActive })}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                      >
                        {u.isActive ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                      </button>
                      <button
                        title="Delete"
                        onClick={() => setDeleteTarget(u)}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite Modal */}
      <Modal open={showInvite} onClose={() => setShowInvite(false)} title="Invite User" description="Create a new team member.">
        <div className="space-y-4">
          <Input label="Email" type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name" value={inviteForm.firstName} onChange={(e) => setInviteForm((f) => ({ ...f, firstName: e.target.value }))} />
            <Input label="Last Name" value={inviteForm.lastName} onChange={(e) => setInviteForm((f) => ({ ...f, lastName: e.target.value }))} />
          </div>
          <Input label="Password" type="password" value={inviteForm.password} onChange={(e) => setInviteForm((f) => ({ ...f, password: e.target.value }))} hint="Minimum 8 characters" />
          {roles.length > 0 && (
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-zinc-700">Roles</label>
              <div className="flex flex-wrap gap-2">
                {roles.map((r) => (
                  <label key={r.id} className="flex items-center gap-1.5 text-sm text-zinc-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inviteRoleIds.includes(r.id)}
                      onChange={(e) => {
                        if (e.target.checked) setInviteRoleIds((prev) => [...prev, r.id]);
                        else setInviteRoleIds((prev) => prev.filter((x) => x !== r.id));
                      }}
                      className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowInvite(false)}>Cancel</Button>
          <Button size="sm" loading={inviteMutation.isPending} onClick={handleInvite} disabled={!inviteForm.email || !inviteForm.password}>
            Create User
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editingUser} onClose={() => setEditingUser(null)} title="Edit User" description={editingUser?.email}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name" value={editForm.firstName} onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))} />
            <Input label="Last Name" value={editForm.lastName} onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))} />
          </div>
          <Input label="Email" type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
          <Input label="New Password" type="password" value={editForm.password} onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))} hint="Leave empty to keep current password" />
          {roles.length > 0 && (
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-zinc-700">Roles</label>
              <div className="flex flex-wrap gap-2">
                {roles.map((r) => (
                  <label key={r.id} className="flex items-center gap-1.5 text-sm text-zinc-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editRoleIds.includes(r.id)}
                      onChange={(e) => {
                        if (e.target.checked) setEditRoleIds((prev) => [...prev, r.id]);
                        else setEditRoleIds((prev) => prev.filter((x) => x !== r.id));
                      }}
                      className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setEditingUser(null)}>Cancel</Button>
          <Button size="sm" loading={updateMutation.isPending} onClick={handleSaveEdit}>
            Save Changes
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete User"
        message={`Are you sure you want to delete ${deleteTarget?.email ?? 'this user'}? This action cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 2: Roles
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface RoleForm {
  name: string;
  description: string;
  permissions: string[];
}

const EMPTY_ROLE_FORM: RoleForm = { name: '', description: '', permissions: [] };

function RolesTab({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState<RoleForm>(EMPTY_ROLE_FORM);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);

  const { data: roles = [], isLoading } = useQuery<Role[]>({
    queryKey: ['team-roles', tenantId],
    queryFn: () => api.get(`/sa/tenants/${tenantId}/roles`).then((r) => r.data),
  });

  const { data: users = [] } = useQuery<TenantUser[]>({
    queryKey: ['team-users', tenantId],
    queryFn: () => api.get(`/sa/tenants/${tenantId}/users`).then((r) => r.data),
  });

  const { data: templates = [] } = useQuery<RoleTemplate[]>({
    queryKey: ['role-templates', tenantId],
    queryFn: () => api.get(`/sa/tenants/${tenantId}/roles/templates`).then((r) => r.data),
    enabled: showTemplates,
  });

  // Count users per role
  const userCountByRole = useMemo(() => {
    const map: Record<string, number> = {};
    for (const u of users) {
      for (const r of u.roles) {
        map[r.id] = (map[r.id] ?? 0) + 1;
      }
    }
    return map;
  }, [users]);

  const createMutation = useMutation({
    mutationFn: (data: RoleForm) => api.post(`/sa/tenants/${tenantId}/roles`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-roles', tenantId] });
      setShowCreate(false);
      setRoleForm(EMPTY_ROLE_FORM);
      toast.success('Role created');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ roleId, data }: { roleId: string; data: RoleForm }) =>
      api.patch(`/sa/tenants/${tenantId}/roles/${roleId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-roles', tenantId] });
      setEditingRole(null);
      toast.success('Role updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (roleId: string) => api.delete(`/sa/tenants/${tenantId}/roles/${roleId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-roles', tenantId] });
      setDeleteTarget(null);
      toast.success('Role deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const createFromTemplateMutation = useMutation({
    mutationFn: (templateKey: string) =>
      api.post(`/sa/tenants/${tenantId}/roles/from-template`, { templateKey }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-roles', tenantId] });
      setShowTemplates(false);
      toast.success('Role created from template');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function openCreate() {
    setRoleForm(EMPTY_ROLE_FORM);
    setShowCreate(true);
  }

  function openEdit(role: Role) {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description ?? '',
      permissions: [...role.permissions],
    });
  }

  function togglePermission(perm: string) {
    setRoleForm((f) => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter((p) => p !== perm)
        : [...f.permissions, perm],
    }));
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="secondary" size="sm" onClick={() => setShowTemplates(true)}>
          <Copy size={14} /> From Template
        </Button>
        <Button size="sm" onClick={openCreate}>
          <Plus size={14} /> New Role
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <SkeletonRows count={4} cols={5} />
      ) : roles.length === 0 ? (
        <EmptyState icon={<ShieldCheck size={26} />} title="No roles" description="Create your first role or use a template." />
      ) : (
        <div className="border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500 text-left text-xs font-medium">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Description</th>
                <th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Users</th>
                <th className="px-4 py-2.5 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {roles.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-50/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-zinc-900">{r.name}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs max-w-xs truncate">{r.description ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge variant={r.isSystem ? 'default' : 'info'}>
                      {r.isSystem ? 'System' : 'Custom'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 tabular-nums">{userCountByRole[r.id] ?? 0}</td>
                  <td className="px-4 py-3">
                    {r.isSystem ? (
                      <span className="text-xs text-zinc-300">Read-only</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          title="Edit"
                          onClick={() => openEdit(r)}
                          className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => setDeleteTarget(r)}
                          className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Role Modal */}
      <Modal
        open={showCreate || !!editingRole}
        onClose={() => { setShowCreate(false); setEditingRole(null); }}
        title={editingRole ? 'Edit Role' : 'New Role'}
        size="lg"
      >
        <div className="space-y-4">
          <Input label="Name" value={roleForm.name} onChange={(e) => setRoleForm((f) => ({ ...f, name: e.target.value }))} />
          <Input label="Description" value={roleForm.description} onChange={(e) => setRoleForm((f) => ({ ...f, description: e.target.value }))} />

          {/* Permission Matrix */}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-zinc-700">Permissions</label>
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-zinc-50 text-zinc-500">
                    <th className="px-3 py-2 text-left font-medium">Resource</th>
                    {/* Dynamically show unique actions across all resources */}
                    {(() => {
                      const allActions = new Set<string>();
                      Object.values(RESOURCE_ACTIONS).forEach((actions) => {
                        (actions as readonly string[]).forEach((a) => allActions.add(a));
                      });
                      return Array.from(allActions).map((action) => (
                        <th key={action} className="px-2 py-2 text-center font-medium capitalize">{action}</th>
                      ));
                    })()}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {(Object.keys(RESOURCE_ACTIONS) as Resource[]).map((resource) => {
                    const actions = RESOURCE_ACTIONS[resource] as readonly string[];
                    const allActions = new Set<string>();
                    Object.values(RESOURCE_ACTIONS).forEach((a) => {
                      (a as readonly string[]).forEach((x) => allActions.add(x));
                    });
                    const allActionsList = Array.from(allActions);
                    return (
                      <tr key={resource} className="hover:bg-zinc-50/50">
                        <td className="px-3 py-2 font-medium text-zinc-700 capitalize">{resource}</td>
                        {allActionsList.map((action) => {
                          const perm = `${resource}.${action}`;
                          const available = actions.includes(action);
                          return (
                            <td key={action} className="px-2 py-2 text-center">
                              {available ? (
                                <input
                                  type="checkbox"
                                  checked={roleForm.permissions.includes(perm)}
                                  onChange={() => togglePermission(perm)}
                                  className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                                />
                              ) : (
                                <span className="text-zinc-200">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setEditingRole(null); }}>Cancel</Button>
          <Button
            size="sm"
            loading={createMutation.isPending || updateMutation.isPending}
            disabled={!roleForm.name || roleForm.permissions.length === 0}
            onClick={() => {
              if (editingRole) {
                updateMutation.mutate({ roleId: editingRole.id, data: roleForm });
              } else {
                createMutation.mutate(roleForm);
              }
            }}
          >
            {editingRole ? 'Save Changes' : 'Create Role'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Templates Modal */}
      <Modal open={showTemplates} onClose={() => setShowTemplates(false)} title="Create from Template" description="Select a predefined role template.">
        <div className="space-y-2">
          {templates.length === 0 ? (
            <p className="text-sm text-zinc-400 py-4 text-center">Loading templates...</p>
          ) : (
            templates.map((t) => (
              <div key={t.key} className="flex items-center justify-between p-3 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900">{t.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{t.description}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {t.permissions.slice(0, 5).map((p) => (
                      <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>
                    ))}
                    {t.permissions.length > 5 && (
                      <Badge variant="outline" className="text-[10px]">+{t.permissions.length - 5} more</Badge>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={createFromTemplateMutation.isPending && createFromTemplateMutation.variables === t.key}
                  onClick={() => createFromTemplateMutation.mutate(t.key)}
                >
                  Use
                </Button>
              </div>
            ))
          )}
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowTemplates(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${deleteTarget?.name ?? ''}"? Users assigned to this role will lose those permissions.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 3: Audit Log
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const PAGE_SIZE = 50;

const AUDIT_ACTION_OPTIONS = [
  'user.login',
  'user.create',
  'user.update',
  'user.delete',
  'user.role_assign',
  'user.role_revoke',
  'role.create',
  'role.update',
  'role.delete',
  'campaigns.create',
  'campaigns.update',
  'campaigns.delete',
  'campaigns.send',
  'contacts.create',
  'contacts.update',
  'contacts.delete',
  'contacts.import',
  'automations.create',
  'automations.update',
  'automations.delete',
  'settings.update',
  'loyalty.update',
  'cms.create',
  'cms.update',
  'cms.delete',
];

function AuditTab({ tenantId }: { tenantId: string }) {
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(page * PAGE_SIZE));
    if (actionFilter) p.set('action', actionFilter);
    if (dateFrom) p.set('from', new Date(dateFrom).toISOString());
    if (dateTo) p.set('to', new Date(dateTo + 'T23:59:59').toISOString());
    return p.toString();
  }, [page, actionFilter, dateFrom, dateTo]);

  const { data, isLoading } = useQuery<AuditLogsResponse>({
    queryKey: ['audit-logs', tenantId, queryParams],
    queryFn: () => api.get(`/sa/tenants/${tenantId}/audit-logs?${queryParams}`).then((r) => r.data),
  });

  const logs = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <>
      {/* Filters */}
      <div className="flex items-end gap-3 mb-4 flex-wrap">
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-zinc-700">Action</label>
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
            className="h-9 px-3 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent cursor-pointer"
          >
            <option value="">All actions</option>
            {AUDIT_ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-zinc-700">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            className="h-9 px-3 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-zinc-700">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            className="h-9 px-3 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </div>
        {(actionFilter || dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={() => { setActionFilter(''); setDateFrom(''); setDateTo(''); setPage(0); }}>
            <X size={13} /> Clear
          </Button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <SkeletonRows count={8} cols={6} />
      ) : logs.length === 0 ? (
        <EmptyState icon={<ScrollText size={26} />} title="No audit logs" description="Activity will appear here as actions are performed." />
      ) : (
        <>
          <div className="border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 text-zinc-500 text-left text-xs font-medium">
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">User</th>
                  <th className="px-4 py-2.5">Action</th>
                  <th className="px-4 py-2.5">Resource Type</th>
                  <th className="px-4 py-2.5">Resource ID</th>
                  <th className="px-4 py-2.5">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-zinc-50/50 transition-colors">
                    <td className="px-4 py-3 text-zinc-600 text-xs whitespace-nowrap">{formatDate(log.createdAt)}</td>
                    <td className="px-4 py-3 text-zinc-700 text-xs">{log.userEmail ?? log.userId ?? '—'}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">{log.action}</Badge>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{log.resourceType ?? '—'}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{log.resourceId ? log.resourceId.slice(0, 8) + '...' : '—'}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{log.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-zinc-500">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
